import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ProductCarousel } from '@noths/polaris-client-ribbons-design-system';

import { selectPersonalisation } from 'src/redux/personalisation/selectors';
import { selectProduct } from 'src/redux/product/selectors';
import { selectUser } from 'src/redux/user/selectors';
import { getServingConfig } from '../helpers/servingConfigHelper';
import { getTitle } from '../helpers/titleHelper';
import { useListingsRecommendations } from '../hook/useListingsRecommendations';
import {
  trackRecommendedProductClick,
  trackRecommendedProductsCarouselNavigation,
  trackRecommendedProductsImpression,
} from '../tracking/recommendedProductCarouselEvents';
import type { ListingsAPIRecommendationPlacement } from '../types';

export interface RecommendedProductsCarouselContainerProps {
  readonly enableFreeDomesticDeliveryLabels?: boolean;
  readonly hideLabelIcons?: boolean;
  readonly placementType: ListingsAPIRecommendationPlacement;
}

export const RecommendedProductsCarouselContainer = ({
  enableFreeDomesticDeliveryLabels,
  hideLabelIcons,
  placementType,
}: RecommendedProductsCarouselContainerProps) => {
  const personalisation = useSelector(selectPersonalisation);
  const user = useSelector(selectUser);
  const product = useSelector(selectProduct);

  const { currencyCode, gaVisitorId: visitorId } = user;
  const partnerShortCode = product.partner.shortcode;
  const productCode = product.code.toString();
  const servingConfig = getServingConfig(placementType);
  const title = getTitle(placementType);

  const { recommendedProducts } = useListingsRecommendations({
    currencyCode,
    partnerShortCode,
    productCode,
    placementType,
    servingConfig,
    visitorId,
  });

  const onVisible = useCallback(() => {
    if (recommendedProducts?.length === 0) {
      return;
    }

    trackRecommendedProductsImpression({
      personalisation,
      products: recommendedProducts,
      placementStrategy: servingConfig,
      placementType,
    });
  }, [personalisation, recommendedProducts, placementType, servingConfig]);

  const onProductClick = useCallback(
    (evt: React.MouseEvent, { index }: { index: number }) => {
      if (!recommendedProducts || recommendedProducts?.length === 0) {
        return;
      }

      trackRecommendedProductClick(evt, {
        personalisation,
        product: recommendedProducts[index],
        placementStrategy: servingConfig,
        productIndex: index,
        placementType,
      });
    },
    [personalisation, recommendedProducts, placementType, servingConfig],
  );

  const onNavigation = useCallback(
    (swiped: boolean, control: 'next' | 'prev') => {
      trackRecommendedProductsCarouselNavigation({
        placementStrategy: servingConfig,
        placementType,
        navigation: {
          type: swiped ? 'Scroll' : 'Arrow Click',
          direction: control === 'next' ? 'Right' : 'Left',
        },
      });
    },
    [placementType, servingConfig],
  );

  return (
    <ProductCarousel
      currency={user.currencyCode}
      enableFreeDomesticDeliveryLabels={enableFreeDomesticDeliveryLabels}
      hideLabelIcons={hideLabelIcons}
      lazyImages
      onNavigation={onNavigation}
      onProductClick={onProductClick}
      onVisible={onVisible}
      products={recommendedProducts}
      title={title}
    />
  );
};
